import has from '@dojo/framework/core/has';

import { get_topic, has_topic } from 'cache-store';
import * as moment from 'moment';

import { chunk, delay } from '@holmescorp/common';
import { topicChange } from '../LccStore';
import { svc } from './booksvc';
import { TopicDef } from '../interfaces';
import { tgfkeys, childmap } from './loadroots';

const topic_getter = (params: { key: string; version: string; language: string }): Promise<TopicDef> => {
	const { key: topicname, version, language } = params;
	return svc.get_topic({ topicname, version, language });
};

const related_getter = (params: { key: string; version: string; language: string }): Promise<string[]> => {
	const { key: topicid, version, language } = params;
	return svc.related_topics({ topicid, version, language });
};

const activeTopic = new Map<string, Promise<TopicDef>>();
const topicAccess = new Map<string, moment.Moment>();

/**
 * don't hold topic promises forever to keep from leaking memory
 */
const cleaner = () => {
	const now = moment();
	topicAccess.forEach((access, key) => {
		if (now.diff(access, 'seconds') > 30) {
			activeTopic.delete(key);
			topicAccess.delete(key);
		}
	});
	setTimeout(cleaner, 60 * 1000);
};

cleaner();

/**
 * Load a topic via cache. The load is protected from being multi-pumped by widgets.
 * (Otherwise, multiple copies are loaded into cache.)
 *
 * @param topicname
 * @param version
 * @param language
 *
 * @returns Promise<TopicDef>
 */
export async function load_topic(topicname: string, version: string, language: string): Promise<TopicDef> {
	const rkey = `${topicname}.${version}.${language}`;
	topicAccess.set(rkey, moment());

	if (!activeTopic.has(rkey)) {
		activeTopic.set(
			rkey,
			get_topic({
				key: topicname,
				version,
				language,
				topic_getter
			})
		);
	}

	const topic = await activeTopic.get(rkey)!;
	topicChange({ topic });
	return topic;
}

const conditionally_get_topic = async ({
	key,
	version,
	language,
	topic_getter
}: {
	key: string;
	version: string;
	language: string;
	topic_getter: any;
	related_getter?: any;
}) => {
	const is_topic = await has_topic({ key, version, language });
	if (!is_topic) {
		await get_topic({ key, version, language, topic_getter, related_getter });
	}
	return true;
};

const conditionally_get_images = async ({ version, language }: { version: string; language: string }) => {
	const images = await svc.get_images({ version, language });
	const imageChunks = chunk(images, 8);
	for (const theChunk of imageChunks) {
		const promises = theChunk.map((image) => fetch(image));
		await Promise.all(promises);
		await delay(5000);
	}
};

export const cache_all_topics = (version: string, language: string): void => {
	// conditionally_get_images({ version, language });

	if (!has('no-explore-study')) {
		tgfkeys.forEach(async (k) => {
			const kids = childmap.get(k)!;
			kids.forEach(async (kid) => {
				await conditionally_get_topic({ key: kid, version, language, topic_getter });
				childmap.get(kid)!.forEach(async (topicname) => {
					await conditionally_get_topic({ key: topicname, version, language, topic_getter, related_getter });
				});
			});
		});
	}
};
